'use client';

import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * Drag-down-to-close for the hand-rolled bottom sheets.
 *
 * Sheets built on `components/ui/BottomSheet` already drag from their header;
 * the ones written by hand only closed on a tap outside, while the expected
 * gesture on a phone is to push the card back down. Same pointer-event approach
 * as `BottomSheet` so both behave alike (and work with a mouse too).
 *
 * The gesture is not armed when the pointer lands on a control (a drag there
 * means pressing it, or selecting text) or inside content that is itself
 * scrolled — dragging there scrolls that content.
 */
const DISMISS_DISTANCE_PX = 96;
const DISMISS_VELOCITY_PX_PER_MS = 0.45;
const RETURN_TRANSITION = 'transform 200ms ease-out';
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="button"]';

function shouldIgnoreGesture(target: EventTarget | null, panel: HTMLElement | null): boolean {
  const element = target instanceof HTMLElement ? target : null;
  if (!element) {
    return false;
  }
  if (element.closest(INTERACTIVE_SELECTOR)) {
    return true;
  }

  let node: HTMLElement | null = element;
  while (node && node !== panel) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight && node.scrollTop > 0) {
      return true;
    }
    node = node.parentElement;
  }

  return false;
}

export function useSwipeToDismiss(onDismiss: () => void, { enabled = true }: { enabled?: boolean } = {}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; y: number; at: number } | null>(null);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!enabled || dragRef.current || shouldIgnoreGesture(event.target, panelRef.current)) {
        return;
      }

      dragRef.current = { pointerId: event.pointerId, y: event.clientY, at: event.timeStamp };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture can fail on some browsers; dragging still works without it.
      }
    },
    [enabled],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    // Downwards only: an upward drag is the user reaching for the top of the sheet.
    const distance = Math.max(0, event.clientY - drag.y);
    setDragging(distance > 0);
    setOffset(distance);
  }, []);

  const settle = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      dragRef.current = null;
      setDragging(false);
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore release errors from browsers that do not support pointer capture.
      }

      const elapsed = Math.max(1, event.timeStamp - drag.at);
      const flicked = offset > 24 && offset / elapsed > DISMISS_VELOCITY_PX_PER_MS;
      if (offset > DISMISS_DISTANCE_PX || flicked) {
        setOffset(0);
        onDismiss();
        return;
      }
      setOffset(0);
    },
    [offset, onDismiss],
  );

  return {
    dragging,
    panelProps: {
      ref: panelRef,
      onPointerDown,
      onPointerMove,
      onPointerUp: settle,
      onPointerCancel: settle,
      style: {
        transform: offset > 0 ? `translateY(${offset}px)` : undefined,
        transition: dragging ? 'none' : RETURN_TRANSITION,
        touchAction: 'pan-y' as const,
      },
    },
  };
}
